import {
  useCapTableTransactions,
  useCompanyData,
  useGetWorkRequestsByCompany,
  useStockClasses,
} from '@commenda/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Icons,
} from '@commenda/kakashi';
import { TEquityInfoKS } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { IncorporationRequestStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import {
  IncorporationFormHeading,
  SubmitWorkRequestModalKS,
  SummaryFieldKS,
} from '../../common';
import { calculateStakeholdersEquityMap } from '../../common/corporateStakeholder';
import CapTable from '../../EquityInfo/CapTable';
import { CompanyValuationSummary } from '../../EquityInfo/CompanyValuationSummary';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';
import { generateOwnershipSummaryTableRowsForUAE } from '../Equity/utils';
import { TUAEIncorporationRequestData } from '../types';
import { SummaryBusinessDetails } from './BusinessDetails';
import { SummaryEntityNames } from './EntityNames';
import { UAEDirectorField } from './UAEDirectorField';

type TSummaryProps = {
  workRequest: WorkRequestResponseType & {
    requestData: TUAEIncorporationRequestData;
  };
  next: () => void;
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  back: () => void;
  hideActionButtons?: boolean;
  setActiveStep: (itemIndex: number) => void;
  companyId: number;
};

export const UAESummary = ({
  workRequest,
  hideActionButtons = false,
  setActiveStep,
  setNotification,
  companyId,
}: TSummaryProps) => {
  const { mutate: mutateCompanyData } = useCompanyData(companyId);
  const { mutate: mutateWorkRequests } = useGetWorkRequestsByCompany(companyId);

  const router = useRouter();

  const { data: capTableTransactions } = useCapTableTransactions(
    workRequest.corporationId
  );
  const { data: stockClasses } = useStockClasses(workRequest.corporationId);

  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });

  const [ownershipSummaryTableData, setOwnershipSummaryTableData] = useState<
    {
      id: string;
      name: string;
      ownership: string;
      actualOwnership: string;
      esopContribution: string;
      shares: string;
    }[]
  >([]);

  const [selectedStakeholders, setSelectedStakeholders] =
    useState<TShareholderVsSharePercentageMap>();

  // we can pick the first stock class here on the corporation level, as when the incorporation process is ongoing
  // we will only have one stock class
  const stockClass = stockClasses?.[0];

  const { id } = router.query as { id: string };

  const [isSubmitting, setSubmitting] = useState(false);

  const getOwnershipSummaryTable = () => {
    const ownershipSummaryTableRows = generateOwnershipSummaryTableRowsForUAE({
      equityInfo,
      selectedStakeholders,
      corporationType: workRequest?.requestData?.corporationType,
    });

    setOwnershipSummaryTableData(ownershipSummaryTableRows);
  };

  const onSubmit = async () => {
    setSubmitting(true);

    const capTableSnapshot = { capTableTransactions, stockClass };

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
          requestData: {
            capTableSnapshot,
          },
        },
      },
      undefined
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    await mutateWorkRequests();
    await mutateCompanyData();
  };

  useEffect(() => {
    getOwnershipSummaryTable();
  }, [equityInfo, selectedStakeholders, capTableTransactions]);

  useEffect(() => {
    if (!stockClass) return;
    if (!capTableTransactions) return;

    const { shareRetained, stakeholdersEquityMap, authorizedShares } =
      calculateStakeholdersEquityMap({
        capTableTransactions,
        stockClass,
      });

    setEquityInfo({
      shareCount: authorizedShares || 10_000_000,
      price:
        stockClass?.additionalInformation?.pricePerShare?.amount || 0.00001,
      shareRetained,
    });

    if (capTableTransactions?.length > 0)
      setSelectedStakeholders(stakeholdersEquityMap);
  }, [workRequest, capTableTransactions, stockClass]);

  return (
    <div className="flex flex-col gap-10">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please verify the following information before submitting. Once submitted, you may not be able to make changes."
      />

      {/* Business Description */}
      <SummaryBusinessDetails
        requestData={workRequest?.requestData}
        hideActionButtons={hideActionButtons}
        setActiveStep={setActiveStep}
      />

      {/* Entity names */}
      <SummaryEntityNames
        requestData={workRequest?.requestData}
        hideActionButtons={hideActionButtons}
        setActiveStep={setActiveStep}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="body-16-bold text-text-primary">
            Shareholders and Key Position Holders
          </h4>
          {hideActionButtons ? null : (
            <Icons.Edit01
              className="cursor-pointer"
              onClick={() => setActiveStep(2)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {workRequest?.directors
              ?.filter((eachDirector) =>
                eachDirector.corporationHasDirector.find(
                  (chd) =>
                    chd.corporationId === workRequest.corporationId &&
                    chd.directorId === eachDirector.id &&
                    (chd.additionalDirectorInformation?.isDirector ||
                      capTableTransactions?.some(
                        (tx) =>
                          tx.stakeholding?.stakeholder?.director?.id ===
                          eachDirector.id
                      ) ||
                      chd.additionalDirectorInformation?.boardPosition?.length)
                )
              )
              .map((dir) => (
                <AccordionItem
                  key={dir.id}
                  className="AccordionItem border border-border-primary rounded-md"
                  value={dir.id?.toString()}
                >
                  <AccordionTrigger className="w-full ">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4 pt-0">
                    <UAEDirectorField
                      key={dir.id}
                      director={dir}
                      data={workRequest}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h4 className="txt-medium-bold">Company Ownership</h4>
        <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm p-6">
          <SummaryFieldKS
            heading="Paid Up Capital"
            value={`AED ${workRequest?.requestData?.paidUpCapital}`}
          />
        </div>
        <div className="flex flex-col pt-2 gap-4 mt-4">
          <CompanyValuationSummary
            numberOfShares={equityInfo.shareCount}
            sharePrice={equityInfo.price}
            currency={{
              countryCode: 'en-AE',
              name: 'AED',
              style: 'AED',
            }}
            helpTexts={{
              mainHeading: 'Paid Up Capital',
            }}
          />
          <CapTable
            data={ownershipSummaryTableData}
            showEsopPool={false}
            isNumberOfSharesApplicable
          />
        </div>
      </div>

      {hideActionButtons ? (
        <Button className="w-fit" onClick={() => router.push('/')}>
          <Icons.ArrowRight className="rotate-180 mr-2 h-4 w-4" />
          <span>Back to Home</span>
        </Button>
      ) : (
        <div className="flex gap-4">
          <Button variant="secondary" onClick={() => setActiveStep(3)}>
            <Icons.ArrowRight className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
            locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  );
};
